import { useAnalytics } from "@hooks/useAnalytics";
import { useHelper } from "@hooks/useHelper";
import { useReviewContext } from "@hooks/useReviewSnippet";
import { useRoutes } from "@hooks/useRoutes";
import { useShopify } from "@hooks/useShopify";
import {
  type Badge,
  type ComponentProps,
  type ResolvedLink,
} from "@ts/components";
import {
  ElasticCollection,
  ElasticImage,
  ElasticProduct,
} from "@usereactify/search";
import React, { FC, memo, useEffect, useMemo } from "react";

import { useWishlistToggle } from "~/hooks/useWishlistNew";

export type SearchResultsProductCardInputProps = ComponentProps & {
  pagePosition: number;
  collection: ElasticCollection;
  product: ElasticProduct;
  dataLayerViewItemList?: { item_list_id?: string; item_list_name?: string };
};

export type SearchResultsProductCardOutputProps = Omit<
  SearchResultsProductCardInputProps,
  "pagePosition"
> & {
  badge: Badge;
  hiddenTitle: string;
  hoverImage: ElasticImage;
  image: ElasticImage;
  isGiftCard: boolean;
  link: ResolvedLink;
  onClick: () => void;
  productGroup: string;
  saleTag: string;
  subTitle: string;
  tags: string[];
  productName: string;
  isInWishlist: boolean;
  toggleWishlist: () => void;
};

export const withSearchResultsProductCard = (
  Component: FC<SearchResultsProductCardOutputProps>,
) =>
  memo(
    ({
      name = "SearchResultsProductCard",
      product,
      collection,
      pagePosition,
      dataLayerViewItemList,
    }: SearchResultsProductCardInputProps) => {
      const { trackProductClick } = useAnalytics();
      const { position } = useHelper();
      const { addProductToQueue } = useReviewContext();
      const { formatProductGroup, getSaleTagFromTags, getBadgeFromTags } =
        useShopify();
      const { linkResolver } = useRoutes();
      const { tags } = product || [];
      const productName = product.metafields
        ? product.metafields.find(
            (metafield) => metafield?.key === "netsuite_style_name",
          )?.value
        : "";
      const productStatus = product.metafields
        ? product.metafields.find(
            (metafield) => metafield?.key === "netsuite_status",
          )?.value
        : "";
      const hasColourOption = product.options.some(
        (item) => item.includes("Color") || item.includes("Colour"),
      );
      const subTitle =
        hasColourOption && product.variant_options
          ? product.variant_options[0]
          : "";
      const hiddenTitle = product?.title
        ?.replace(productName, ``)
        ?.replace(subTitle, ``)
        ?.trim();
      const link = {
        ...linkResolver(product),
        url: product.url,
      };
      const image = {
        src: product?.images?.[0]?.src,
      };
      const hoverImage = {
        src: product?.images?.[1]?.src,
      };

      const availableForSale = product.inventory_total
        ? product.inventory_total >= 1
        : false;
      const isGiftCard = tags?.includes("type:giftcard");
      const colour = tags
        ?.find((tag) => tag.includes("colour:"))
        ?.split(":")[1];
      const saleTag = getSaleTagFromTags(tags);
      const badge = getBadgeFromTags(tags, productStatus, availableForSale);
      const productGroup = formatProductGroup(tags);

      const handleClick = () => {
        const scrollPosition = position();
        localStorage.setItem("collection_scroll_position", `${scrollPosition}`);
        trackProductClick({
          product,
          colour,
          index: pagePosition,
          dataLayer: {
            item_list_id: dataLayerViewItemList?.item_list_id,
            item_list_name: dataLayerViewItemList?.item_list_name,
          },
        });
      };

      useEffect(() => {
        addProductToQueue(product.handle);
      }, [product]);

      const { isInWishlist, toggleWishlist } = useWishlistToggle({
        productHandle: product.handle,
      });

      Component.displayName = name;

      return useMemo(
        () => (
          <Component
            badge={badge}
            collection={collection}
            hiddenTitle={hiddenTitle}
            hoverImage={hoverImage}
            image={image}
            isGiftCard={isGiftCard}
            link={link}
            onClick={handleClick}
            product={product}
            productGroup={productGroup}
            saleTag={saleTag}
            subTitle={subTitle}
            title={productName}
            tags={tags}
            isInWishlist={!!isInWishlist}
            toggleWishlist={toggleWishlist}
          />
        ),
        [product, isInWishlist],
      );
    },
  );
